import i18next from 'i18next'
import fetch from 'node-fetch'
import { Arg, Ctx, Directive, Mutation, Resolver } from 'type-graphql'
import { In } from 'typeorm'

import { getEndpointUrl } from '../../utils/config-resolver.js'
import { DataArchive } from './data-archive.js'
import { DataArchivePatch, NewDataArchive } from './data-archive-type.js'

const { config } = require('@things-factory/env')
const crypto = require('crypto')

@Resolver(DataArchive)
export class DataArchiveMutation {
  /**
   * It may use 'func-[dev]-data-set-download' in https://github.com/operatochef/serverless
   * This function requests athena query and save the result as csv.gz in s3
   */
  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => DataArchive, {
    description:
      'Generates a presigned URL for downloading archived data. This mutation initiates the data export process and notifies users when the download is ready.'
  })
  async generatePresignedUrl(
    @Arg('patch') patch: DataArchivePatch,
    @Ctx() context: ResolverContext
  ): Promise<DataArchive> {
    const { domain, user, tx, notify, lng } = context.state
    // const dataArchiveRepo = tx.getRepository(DataArchive)
    const t = i18next.getFixedT(lng, 'translations')
    const {
      dataset: {
        endpoint,
        datasource: { database }
      }
    } = config.get('dataArchive')

    const body = JSON.stringify({
      domain: domain?.subdomain,
      database,
      ...patch?.requestParams
    })

    const url = getEndpointUrl(endpoint)

    const fetched = await fetch(`${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    })

    const message = {
      receivers: [user.email],
      mode: 'in-app',
      title: '',
      body: '',
      url: ''
    }

    if (fetched.ok) {
      const response = await fetched.json()
      patch.downloadUrl = response['presignedUrl']
      patch.status = 'done'

      message['title'] = t('title.data-archive downloads ready')
      message['url'] = patch.downloadUrl
    } else {
      const error = await fetched.text()
      console.log(error)
      patch.status = 'error'

      message['title'] = t('title.data-archive request failed')
      // may not user-friendly message
      message['body'] = error
    }

    const result = await this.updateDataArchive(patch.id, patch, context)

    notify && notify(message)

    return result
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => DataArchive, {
    description:
      'Creates a new data archive record for storing historical data exports. This mutation tracks the archive request and download status.'
  })
  async createDataArchive(
    @Arg('dataArchive') dataArchive: NewDataArchive,
    @Ctx() context: ResolverContext
  ): Promise<DataArchive> {
    const { domain, user, tx } = context.state
    const dataArchiveRepo = tx.getRepository(DataArchive)

    const result = await dataArchiveRepo.save({
      ...dataArchive,
      domain,
      creator: user,
      updater: user
    })

    return result
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => DataArchive, {
    description:
      'Updates a data archive record with new status, download URL, and request parameters. This mutation tracks the progress of data export operations.'
  })
  async updateDataArchive(
    @Arg('id') id: string,
    @Arg('patch') patch: DataArchivePatch,
    @Ctx() context: ResolverContext
  ): Promise<DataArchive> {
    const { domain, user, tx } = context.state
    const dataArchiveRepo = tx.getRepository(DataArchive)

    const dataArchive = await dataArchiveRepo.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['domain', 'creator', 'updater']
    })

    const result = await dataArchiveRepo.save({
      ...dataArchive,
      ...patch,
      updater: user
    })

    return result
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => [DataArchive], {
    description:
      'Updates multiple data archive records in a single operation. This mutation supports batch processing for efficient archive management.'
  })
  async updateMultipleDataArchive(
    @Arg('patches', type => [DataArchivePatch]) patches: DataArchivePatch[],
    @Ctx() context: any
  ): Promise<DataArchive[]> {
    const { domain, user, tx } = context.state
    const dataArchiveRepo = tx.getRepository(DataArchive)

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')

    if (_createRecords.length > 0) {
      const cuFlag = '+'
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await dataArchiveRepo.save({
          ...newRecord,
          domain,
          creator: user,
          updater: user
        })

        results.push({
          ...result,
          cuFlag
        })
      }
    }

    if (_updateRecords.length > 0) {
      const cuFlag = 'M'
      for (let i = 0; i < _updateRecords.length; i++) {
        const newRecord = _updateRecords[i]
        const dataArchive = await dataArchiveRepo.findOne(newRecord.id, {
          relations: ['domain', 'creator', 'updater']
        })

        const result = await dataArchiveRepo.save({
          ...dataArchive,
          ...newRecord,
          updater: user
        })

        results.push({
          ...result,
          cuFlag
        })
      }
    }

    return results
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description:
      'Deletes a data archive record and removes the associated download files. This mutation cleans up completed or failed archive requests.'
  })
  async deleteDataArchive(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(DataArchive).delete({ domain: { id: domain.id }, id })
    return true
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description:
      'Deletes multiple data archive records in a single operation. This mutation supports batch deletion for efficient cleanup of archive requests.'
  })
  async deleteDataArchives(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(DataArchive).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    return true
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'To import multiple data-archives' })
  async importDataArchives(
    @Arg('dataArchives', type => [DataArchivePatch]) dataArchives: DataArchive[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state
    const dataArchiveRepo = tx.getRepository(DataArchive)

    await Promise.all(
      dataArchives.map(async (dataArchive: DataArchive) => {
        const createdDataArchive: DataArchive = await dataArchiveRepo.save({
          domain,
          ...dataArchive
        })
      })
    )

    return true
  }

  @Directive('@privilege(category: "data-archive", privilege: "mutation", domainOwnerGranted: true)')
  @Directive('@transaction')
  @Mutation(returns => [DataArchive], { description: 'To copy multiple data-archives' })
  async copyDataArchives(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<DataArchive[]> {
    const { domain, user, tx } = context.state
    const dataArchiveRepo = tx.getRepository(DataArchive)

    const originals = await dataArchiveRepo.find({
      where: {
        id: In(ids),
        domain: { id: domain.id }
      },
      relations: ['domain']
    })

    if (originals.length == 0) {
      return []
    }

    var newCopys = originals.map(dataArchive => {
      let dataArchiveId = crypto.randomUUID()

      return {
        ...dataArchive,
        id: dataArchiveId,
        domain,
        creator: user,
        updater: user,
        updatedAt: undefined,
        createdAt: undefined
      }
    })

    var copiedDataArchives = await dataArchiveRepo.save(newCopys)

    return copiedDataArchives
  }
}
